/**
 * ============================================================================
 * Theme Configuration — Dummy backend JSON payload
 * ============================================================================
 *
 * This file simulates a backend API response that provides branding/theme
 * overrides. In production, you would fetch this from your API and pass it
 * to ThemeService.applyTheme().
 *
 * EVERY key in this object maps 1:1 to a CSS custom property in _tokens.css.
 * The key is the CSS variable name (e.g. '--app-color-primary') and the
 * value is any valid CSS value.
 *
 * You can override 0 to 100% of the tokens. Only the keys you include
 * will be overridden — everything else keeps its default from _tokens.css.
 * ============================================================================
 */

/**
 * All possible token keys that can be overridden.
 * This interface is the contract between backend and frontend.
 */
export interface ThemeTokens {
  // ── Colors ──────────────────────────────────────────────────────────────
  '--app-color-primary'?: string;
  '--app-color-primary-hover'?: string;
  '--app-color-primary-light'?: string;
  '--app-color-secondary'?: string;
  '--app-color-secondary-hover'?: string;
  '--app-color-secondary-light'?: string;
  '--app-color-accent'?: string;
  '--app-color-accent-hover'?: string;
  '--app-color-accent-light'?: string;
  '--app-color-success'?: string;
  '--app-color-success-light'?: string;
  '--app-color-warning'?: string;
  '--app-color-warning-light'?: string;
  '--app-color-error'?: string;
  '--app-color-error-light'?: string;
  '--app-color-info'?: string;
  '--app-color-info-light'?: string;
  '--app-color-background'?: string;
  '--app-color-surface'?: string;
  '--app-color-surface-alt'?: string;
  '--app-color-overlay'?: string;
  '--app-color-border'?: string;
  '--app-color-border-strong'?: string;
  '--app-color-border-focus'?: string;
  '--app-color-scrollbar'?: string;
  '--app-color-scrollbar-hover'?: string;
  '--app-color-text'?: string;
  '--app-color-text-muted'?: string;
  '--app-color-text-subtle'?: string;
  '--app-color-text-disabled'?: string;
  '--app-color-text-on-primary'?: string;
  '--app-color-text-on-secondary'?: string;
  '--app-color-text-on-accent'?: string;
  '--app-color-text-on-success'?: string;
  '--app-color-text-on-info'?: string;
  '--app-color-text-on-error'?: string;
  '--app-color-primary-rgb'?: string;
  '--app-color-danger-rgb'?: string;

  // ── Typography ──────────────────────────────────────────────────────────
  '--app-font-sans'?: string;
  '--app-font-serif'?: string;
  '--app-font-mono'?: string;
  '--app-font-heading'?: string;
  '--app-text-xs'?: string;
  '--app-text-sm'?: string;
  '--app-text-base'?: string;
  '--app-text-md'?: string;
  '--app-text-lg'?: string;
  '--app-text-xl'?: string;
  '--app-text-2xl'?: string;
  '--app-text-3xl'?: string;
  '--app-text-4xl'?: string;
  '--app-text-5xl'?: string;
  '--app-font-weight-light'?: string;
  '--app-font-weight-normal'?: string;
  '--app-font-weight-medium'?: string;
  '--app-font-weight-semibold'?: string;
  '--app-font-weight-bold'?: string;
  '--app-font-weight-extrabold'?: string;
  '--app-leading-none'?: string;
  '--app-leading-tight'?: string;
  '--app-leading-snug'?: string;
  '--app-leading-normal'?: string;
  '--app-leading-relaxed'?: string;
  '--app-leading-loose'?: string;
  '--app-tracking-tighter'?: string;
  '--app-tracking-tight'?: string;
  '--app-tracking-normal'?: string;
  '--app-tracking-wide'?: string;
  '--app-tracking-wider'?: string;
  '--app-tracking-widest'?: string;

  // ── Spacing ─────────────────────────────────────────────────────────────
  '--app-spacing'?: string;
  '--app-gap'?: string;

  // ── Border Radius ───────────────────────────────────────────────────────
  '--app-radius-none'?: string;
  '--app-radius-xs'?: string;
  '--app-radius-sm'?: string;
  '--app-radius-md'?: string;
  '--app-radius-lg'?: string;
  '--app-radius-xl'?: string;
  '--app-radius-2xl'?: string;
  '--app-radius-3xl'?: string;
  '--app-radius-full'?: string;

  // ── Shadows ─────────────────────────────────────────────────────────────
  '--app-shadow-none'?: string;
  '--app-shadow-xs'?: string;
  '--app-shadow-sm'?: string;
  '--app-shadow-md'?: string;
  '--app-shadow-lg'?: string;
  '--app-shadow-xl'?: string;
  '--app-shadow-2xl'?: string;

  // ── Transitions & Animation ─────────────────────────────────────────────
  '--app-ease-default'?: string;
  '--app-ease-in'?: string;
  '--app-ease-out'?: string;
  '--app-ease-in-out'?: string;
  '--app-ease-bounce'?: string;
  '--app-duration-fast'?: string;
  '--app-duration-normal'?: string;
  '--app-duration-slow'?: string;
  '--app-duration-slower'?: string;

  // ── Z-Index ─────────────────────────────────────────────────────────────
  '--app-z-base'?: string;
  '--app-z-dropdown'?: string;
  '--app-z-sticky'?: string;
  '--app-z-fixed'?: string;
  '--app-z-overlay'?: string;
  '--app-z-modal'?: string;
  '--app-z-popover'?: string;
  '--app-z-tooltip'?: string;
  '--app-z-toast'?: string;

  // ── Breakpoints ─────────────────────────────────────────────────────────
  '--app-breakpoint-sm'?: string;
  '--app-breakpoint-md'?: string;
  '--app-breakpoint-lg'?: string;
  '--app-breakpoint-xl'?: string;
  '--app-breakpoint-2xl'?: string;

  // ── Container ───────────────────────────────────────────────────────────
  '--app-container-sm'?: string;
  '--app-container-md'?: string;
  '--app-container-lg'?: string;
  '--app-container-xl'?: string;

  // ── Opacity ─────────────────────────────────────────────────────────────
  '--app-opacity-disabled'?: string;
  '--app-opacity-hover'?: string;
  '--app-opacity-overlay'?: string;

  // ── Surface Effects ─────────────────────────────────────────────────────
  '--app-surface-blur'?: string;
  '--app-border-width'?: string;

  // ── Focus Ring ──────────────────────────────────────────────────────────
  '--app-ring-width'?: string;
  '--app-ring-offset'?: string;
  '--app-ring-color'?: string;

  // ── Catch-all for any custom tokens ─────────────────────────────────────
  [key: `--app-${string}`]: string | undefined;
}

/**
 * Dummy backend payload — "Default" theme.
 * This simulates what your /api/theme endpoint returns.
 * Only include keys you want to override.
 */
export const DEFAULT_THEME: ThemeTokens = {
  // '--app-gap': '1rem',
};

/**
 * Example: "Brand A" theme — a completely different look.
 * Demonstrates that you can override as much or as little as you want.
 */
/**
 * Example: "Brand A" theme (removed per user request)
 * The user requested to remove Neo Vibrant.
 */

/**
 * Example: "Dark Mode" theme.
 * Shows how a dark theme is just another set of token overrides.
 */
export const DARK_THEME: ThemeTokens = {
  '--app-color-success': 'oklch(0.72 0.16 150)',
  '--app-color-success-light': 'oklch(0.25 0.04 150)',
  '--app-color-warning': 'oklch(0.80 0.16 75)',
  '--app-color-warning-light': 'oklch(0.25 0.04 75)',
  '--app-color-error': 'oklch(0.72 0.20 27)',
  '--app-color-error-light': 'oklch(0.25 0.05 27)',
  '--app-color-info': 'oklch(0.72 0.13 250)',
  '--app-color-info-light': 'oklch(0.25 0.04 250)',

  '--app-color-background': 'oklch(0.15 0 0)',
  '--app-color-surface': 'oklch(0.20 0 0)',
  '--app-color-surface-alt': 'oklch(0.18 0 0)',
  '--app-color-overlay': 'oklch(0.05 0 0 / 0.7)',

  '--app-color-border': 'oklch(0.30 0 0)',
  '--app-color-border-strong': 'oklch(0.45 0 0)',
  '--app-color-scrollbar': 'oklch(0.40 0 0)',
  '--app-color-scrollbar-hover': 'oklch(0.60 0 0)',

  '--app-color-text': 'oklch(0.93 0 0)',
  '--app-color-text-muted': 'oklch(0.70 0 0)',
  '--app-color-text-subtle': 'oklch(0.55 0 0)',
  '--app-color-text-on-primary': 'oklch(1 0 0)',
  '--app-color-text-on-secondary': 'oklch(1 0 0)',
  '--app-color-text-on-accent': 'oklch(0.15 0 0)',
  '--app-color-text-on-success': 'oklch(1 0 0)',
  '--app-color-text-on-info': 'oklch(1 0 0)',
  '--app-color-text-on-error': 'oklch(1 0 0)',

  '--app-gap': '1rem',
};

/**
 * Example: "Minimal" theme — override only spacing & fonts.
 * Shows you can change just a few tokens to get a different feel.
 */
export const MINIMAL_THEME: ThemeTokens = {
  '--app-spacing': '0.3rem', // Tighter spacing
  '--app-font-sans': '"IBM Plex Sans", system-ui, sans-serif',
  '--app-font-heading': '"IBM Plex Sans", system-ui, sans-serif',
  '--app-font-mono': '"IBM Plex Mono", monospace',
  '--app-radius-sm': '0',
  '--app-radius-md': '0',
  '--app-radius-lg': '2px',
  '--app-radius-xl': '4px',
  '--app-radius-2xl': '4px',
  '--app-shadow-sm': 'none',
  '--app-shadow-md': '0 1px 2px 0 rgb(0 0 0 / 0.05)',
  '--app-shadow-lg': '0 2px 4px 0 rgb(0 0 0 / 0.08)',

  '--app-gap': '1rem',
};

// ============================================================================
// DESIGN SYSTEM THEMES
// ============================================================================

export const CYBER_THEME: ThemeTokens = {
  // Cyber Security Terminal look matches dark mode colors by default
  '--app-color-primary': '#00ff41',
  '--app-color-primary-hover': '#008f11',
  '--app-color-primary-light': '#003b00',
  '--app-color-secondary': '#ff003c',
  '--app-color-secondary-hover': '#aa0028',
  '--app-color-secondary-light': '#440010',
  '--app-color-accent': '#00ffff',
  '--app-color-accent-hover': '#008888',

  '--app-color-background': '#0a0a0a',
  '--app-color-surface': '#111111',
  '--app-color-surface-alt': '#1a1a1a',

  '--app-color-border': '#00ff4140',
  '--app-color-border-strong': '#00ff4180',

  '--app-color-text': '#00ff41',
  '--app-color-text-muted': '#008f11',
  '--app-color-text-subtle': '#003b00',
  '--app-color-text-on-primary': '#000000',

  '--app-color-scrollbar': '#00ff4140',
  '--app-color-scrollbar-hover': '#00ff41',

  '--app-font-sans': '"JetBrains Mono", "Fira Code", monospace',
  '--app-font-heading': '"JetBrains Mono", "Fira Code", monospace',

  '--app-radius-none': '0',
  '--app-radius-xs': '0',
  '--app-radius-sm': '0',
  '--app-radius-md': '0',
  '--app-radius-lg': '0',
  '--app-radius-xl': '0',
  '--app-radius-2xl': '0',
  '--app-radius-3xl': '0',
  '--app-radius-full': '0',

  '--app-shadow-none': 'none',
  '--app-shadow-sm': '0 0 5px #00ff4120',
  '--app-shadow-md': '0 0 10px #00ff4140',
  '--app-shadow-lg': '0 0 15px #00ff4160',
};

export const CYBER_DARK_THEME: ThemeTokens = {
  ...DARK_THEME,
  ...CYBER_THEME, // Cyber theme is naturally dark
};

export const DUOLINGO_THEME: ThemeTokens = {
  // Playful, chunky, rounded look
  '--app-color-primary': '#58cc02', // Duolingo Green
  '--app-color-primary-hover': '#387906',
  '--app-color-primary-light': '#89e219',
  '--app-color-secondary': '#1cb0f6', // Duolingo Blue
  '--app-color-secondary-hover': '#1899d6',
  '--app-color-secondary-light': '#60cbf9',
  '--app-color-accent': '#ffc800', // Duolingo Yellow
  '--app-color-accent-hover': '#e5b400',
  '--app-color-error': '#ff4b4b', // Duolingo Red

  '--app-color-background': '#ffffff',
  '--app-color-surface': '#ffffff',
  '--app-color-surface-alt': '#f7f7f7',

  '--app-color-border': '#e5e5e5',
  '--app-color-border-strong': '#afafaf',

  '--app-color-scrollbar': '#e5e5e5',
  '--app-color-scrollbar-hover': '#afafaf',

  '--app-font-sans': '"Nunito", "Quicksand", system-ui, sans-serif',
  '--app-font-heading': '"Nunito", "Quicksand", system-ui, sans-serif',
  '--app-font-weight-bold': '900', // Extra thick fonts
  '--app-font-weight-semibold': '800',
  '--app-font-weight-medium': '700',
  '--app-font-weight-normal': '700',

  '--app-radius-none': '0',
  '--app-radius-xs': '0.75rem',
  '--app-radius-sm': '1rem',
  '--app-radius-md': '1.25rem',
  '--app-radius-lg': '1.5rem',
  '--app-radius-xl': '2rem',
  '--app-radius-2xl': '2.5rem',
  '--app-radius-3xl': '3rem',
  '--app-radius-full': '9999px',

  '--app-border-width': '2px',

  // Chunky, un-blurred shadows
  '--app-shadow-sm': '0px 2px 0px 0px #e5e5e5',
  '--app-shadow-md': '0px 4px 0px 0px #e5e5e5',
  '--app-shadow-lg': '0px 6px 0px 0px #e5e5e5',
};

export const DUOLINGO_DARK_THEME: ThemeTokens = {
  ...DARK_THEME,
  ...DUOLINGO_THEME,
  '--app-color-background': '#131f24', // Duolingo dark bg
  '--app-color-surface': '#131f24',
  '--app-color-surface-alt': '#202f36',
  '--app-color-border': '#37464f',
  '--app-color-border-strong': '#52656d',
  '--app-shadow-sm': '0px 2px 0px 0px #37464f',
  '--app-shadow-md': '0px 4px 0px 0px #37464f',
  '--app-shadow-lg': '0px 6px 0px 0px #37464f',
  '--app-color-text': '#ffffff',
  '--app-color-text-muted': '#afafaf',
  '--app-color-text-subtle': '#777777',
  '--app-color-scrollbar': '#37464f',
  '--app-color-scrollbar-hover': '#52656d',
};

export const MATERIAL_YOU_THEME: ThemeTokens = {
  // Pastel/Vibrant Google-esque palette
  '--app-color-primary': 'oklch(0.55 0.25 290)',
  '--app-color-primary-light': 'oklch(0.9 0.05 290)',
  '--app-color-secondary': 'oklch(0.65 0.2 140)',
  '--app-color-secondary-light': 'oklch(0.9 0.03 140)',

  // High border radius across the board
  '--app-radius-sm': '0.5rem',
  '--app-radius-md': '1rem',
  '--app-radius-lg': '1.5rem',
  '--app-radius-xl': '2rem',

  // Specific fonts if included, otherwise use default
  '--app-font-sans': '"Roboto", system-ui, sans-serif',

  // Material elevation shadows
  '--app-shadow-sm':
    '0 1px 3px 0 rgba(0,0,0,0.12), 0 1px 2px 0 rgba(0,0,0,0.24)',
  '--app-shadow-md':
    '0 3px 6px 0 rgba(0,0,0,0.16), 0 3px 6px 0 rgba(0,0,0,0.23)',
};

export const MATERIAL_YOU_DARK_THEME: ThemeTokens = {
  ...DARK_THEME,
  ...MATERIAL_YOU_THEME, // inherit radii and font
  '--app-color-primary': 'oklch(0.80 0.15 290)', // lighter for dark mode
  '--app-color-surface': 'oklch(0.25 0.02 290)', // tinted surface
  '--app-shadow-sm': '0 1px 3px 0 rgba(0,0,0,0.4), 0 1px 2px 0 rgba(0,0,0,0.5)',
};

export const BRUTALIST_THEME: ThemeTokens = {
  // Harsh, high-contrast colors
  '--app-color-primary': '#ff0000',
  '--app-color-primary-hover': '#cc0000',
  '--app-color-primary-light': '#ffcccc',
  '--app-color-secondary': '#0000ff',
  '--app-color-secondary-hover': '#0000cc',
  '--app-color-secondary-light': '#ccccff',
  '--app-color-accent': '#ffff00',
  '--app-color-accent-hover': '#cccc00',
  '--app-color-accent-light': '#ffffe6',

  // High contrast surfaces
  '--app-color-background': '#ffffff',
  '--app-color-surface': '#ffffff',
  '--app-color-surface-alt': '#f0f0f0',

  // Heavy borders
  '--app-border-width': '3px',
  '--app-color-border': '#000000',
  '--app-color-border-strong': '#000000',

  // Text
  '--app-color-text': '#000000',
  '--app-color-text-muted': '#333333',
  '--app-color-text-subtle': '#666666',

  // Zero radius
  '--app-radius-none': '0',
  '--app-radius-xs': '0',
  '--app-radius-sm': '0',
  '--app-radius-md': '0',
  '--app-radius-lg': '0',
  '--app-radius-xl': '0',
  '--app-radius-2xl': '0',
  '--app-radius-3xl': '0',
  '--app-radius-full': '0',

  // Hard shadows (offset without blur)
  '--app-shadow-sm': '4px 4px 0px 0px #000000',
  '--app-shadow-md': '8px 8px 0px 0px #000000',
  '--app-shadow-lg': '12px 12px 0px 0px #000000',
  '--app-shadow-xl': '16px 16px 0px 0px #000000',
  '--app-shadow-2xl': '20px 20px 0px 0px #000000',

  // Monospace or harsh typography
  '--app-font-sans':
    '"JetBrains Mono", ui-monospace, SFMono-Regular, monospace',
  '--app-font-heading':
    '"JetBrains Mono", ui-monospace, SFMono-Regular, monospace',
};

export const BRUTALIST_DARK_THEME: ThemeTokens = {
  ...DARK_THEME, // INHERIT the generic dark mode palette (success, info, overlay, etc)

  '--app-color-background': '#000000',
  '--app-color-surface': '#000000',
  '--app-color-surface-alt': '#1a1a1a',

  // Invert the heavy border
  '--app-border-width': '3px',
  '--app-color-border': '#ffffff',
  '--app-color-border-strong': '#ffffff',

  // Invert text
  '--app-color-text': '#ffffff',
  '--app-color-text-muted': '#cccccc',
  '--app-color-text-subtle': '#999999',

  // Hard shadows inverted
  '--app-shadow-sm': '4px 4px 0px 0px #ffffff',
  '--app-shadow-md': '8px 8px 0px 0px #ffffff',
  '--app-shadow-lg': '12px 12px 0px 0px #ffffff',
  '--app-shadow-xl': '16px 16px 0px 0px #ffffff',
  '--app-shadow-2xl': '20px 20px 0px 0px #ffffff',
};
